import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';

const UsageLogModal = ({ part, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    part_id: part?.id,
    service_request_id: null,
    quantity_used: 1,
    unit_number: '',
    technician_name: '',
    usage_notes: '',
    cost_total: 0
  });
  const [serviceRequests, setServiceRequests] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchServiceRequests();
  }, []);

  useEffect(() => {
    const cost = parseFloat(part?.unit_cost || 0) * parseInt(formData?.quantity_used || 0);
    setFormData(prev => ({ ...prev, cost_total: cost }));
  }, [formData?.quantity_used, part?.unit_cost]);

  const fetchServiceRequests = async () => {
    try {
      const { data, error } = await supabase
        ?.from('service_requests')
        ?.select('id, request_number, unit_number, issue_type')
        ?.in('status', ['pending', 'in-progress'])
        ?.order('created_at', { ascending: false })
        ?.limit(20);

      if (error) throw error;
      setServiceRequests(data || []);
    } catch (err) {
      console.error('Error fetching service requests:', err);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (formData?.quantity_used <= 0) {
      alert('Quantity must be greater than 0');
      return;
    }

    if (formData?.quantity_used > part?.quantity) {
      alert(`Cannot use more than available quantity (${part?.quantity})`);
      return;
    }

    setIsSubmitting(true);
    await onSubmit?.(formData);
    setIsSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Log Parts Usage</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {part?.part_name} ({part?.part_number})
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Available Quantity */}
          <div className="bg-muted/30 border border-border rounded-lg p-4">
            <p className="text-sm text-muted-foreground">Available Quantity</p>
            <p className="text-2xl font-bold text-foreground">{part?.quantity}</p>
          </div>

          {/* Quantity Used */}
          <Input
            type="number"
            label="Quantity Used"
            required
            min="1"
            max={part?.quantity}
            value={formData?.quantity_used}
            onChange={(e) => handleChange('quantity_used', parseInt(e?.target?.value) || 0)}
          />

          {/* Service Request */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Service Request (Optional)
            </label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              value={formData?.service_request_id || ''}
              onChange={(e) => handleChange('service_request_id', e?.target?.value || null)}
            >
              <option value="">None</option>
              {serviceRequests?.map((req) => (
                <option key={req?.id} value={req?.id}>
                  {req?.request_number} - {req?.unit_number} ({req?.issue_type})
                </option>
              ))}
            </select>
          </div>

          {/* Unit Number */}
          <Input
            label="Unit Number"
            placeholder="e.g., UNIT-1234"
            value={formData?.unit_number}
            onChange={(e) => handleChange('unit_number', e?.target?.value)}
          />

          {/* Technician Name */}
          <Input
            label="Technician Name"
            placeholder="Enter technician name"
            value={formData?.technician_name}
            onChange={(e) => handleChange('technician_name', e?.target?.value)}
          />

          {/* Usage Notes */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Usage Notes
            </label>
            <textarea
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              placeholder="Add any notes about this usage..."
              value={formData?.usage_notes}
              onChange={(e) => handleChange('usage_notes', e?.target?.value)}
              rows={3}
            />
          </div>

          {/* Cost Total */}
          <div className="bg-primary/10 border border-primary rounded-lg p-4">
            <p className="text-sm text-muted-foreground">Total Cost</p>
            <p className="text-2xl font-bold text-primary">${formData?.cost_total?.toFixed(2)}</p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting}>
              <Icon name="Check" size={18} className="mr-2" />
              Log Usage
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UsageLogModal;
